// frontend/klaes-app/src/components/FilmFavs/index.jsx
import './styleUtils/variables.css';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  FiSearch, FiPlus, FiShuffle, FiTrendingUp, FiUser,
  FiAward, FiFileText, FiArrowUp,
} from 'react-icons/fi';
import { useFilmFavsAPI } from './hooks/useFilmFavsAPI';
import { Chip } from './components/Chip';
import { EntryModal } from './components/EntryModal';
import { SearchModal } from './components/SearchModal';
import { RandomViewModal } from './components/RandomViewModal';
import { UserRatingsPopup } from './components/UserRatingsPopup';
import { FilmEntry } from './components/FilmEntry';
import { DeleteConfirmModal } from './components/DeleteConfirmModal';
import { styles } from './styles';
import { apiGetUsers } from './utils/api';

// Scroll-to-Top Button Komponente
const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!isVisible) return null;

  return (
    <button
      className={styles.compScrollToTopBtn}
      onClick={scrollToTop}
      title="Nach oben"
    >
      <FiArrowUp size={24} />
    </button>
  );
};

// Hilfsfunktion zum Normalisieren der Entries (Strings zu Zahlen)
const normalizeEntries = (entries) => {
  if (!Array.isArray(entries)) return [];
  return entries.map(entry => ({
    ...entry,
    current_ranking:
      typeof entry.current_ranking === 'number'
        ? entry.current_ranking
        : parseFloat(entry.current_ranking) || 0,
    user_ratings: Object.fromEntries(
      Object.entries(entry.user_ratings || {}).map(([user, rating]) => [
        user,
        typeof rating === 'number' ? rating : parseFloat(rating) || 0,
      ])
    ),
  }));
};

const FilmFavs = ({ onNavigateHome }) => {
  // ← A6: token mit aus useAuth ziehen
  const { user, token } = useAuth();
  const currentUser = user?.tagname;

  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [users, setUsers] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showRatingsPopup, setShowRatingsPopup] = useState(null);
  const [randomEntry, setRandomEntry] = useState(null);
  const [currentFilter, setCurrentFilter] = useState({
    type: 'both',
    tags: [],
    term: '',
    matchMode: 'or',
  });
  const [sortBy, setSortBy] = useState('average');
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const {
    fetchEntries,
    addEntry,
    updateEntry,
    deleteEntry,
    fetchTags,
    createTag,
    deleteTag,
  } = useFilmFavsAPI();

  useEffect(() => {
    if (!token) return;

    const loadData = async () => {
      try {
        const [entriesData, tagsData, usersData] = await Promise.all([
          fetchEntries().catch(() => []),
          fetchTags().catch(() => []),
          apiGetUsers(token).catch(() => ({})),
        ]);

        setEntries(normalizeEntries(entriesData));
        setAllTags(Array.isArray(tagsData) ? tagsData : []);
        setUsers(usersData || {});
      } catch (error) {
        console.error('Fehler beim Laden:', error);
        setEntries([]);
        setAllTags([]);
        setUsers({});
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchEntries, fetchTags, token]);

  const applyFilter = useCallback(
    (filter, entryList) => {
      let filtered = [...entryList];

      if (filter.type !== 'both') {
        filtered = filtered.filter(e => e.type === filter.type);
      }

      if (filter.term?.trim()) {
        const term = filter.term.toLowerCase();
        filtered = filtered.filter(
          e =>
            e.name.toLowerCase().includes(term) ||
            (e.description || '').toLowerCase().includes(term)
        );
      }

      if (filter.tags?.length) {
        if (filter.matchMode === 'and') {
          filtered = filtered.filter(e =>
            filter.tags.every(filterTag =>
              e.tags.some(t => t.name === filterTag.name)
            )
          );
        } else {
          filtered = filtered.filter(e =>
            filter.tags.some(filterTag =>
              e.tags.some(t => t.name === filterTag.name)
            )
          );
        }
      }

      if (sortBy === 'average') {
        filtered.sort((a, b) => b.current_ranking - a.current_ranking);
      } else {
        filtered.sort(
          (a, b) =>
            (b.user_ratings?.[currentUser] || 0) -
            (a.user_ratings?.[currentUser] || 0)
        );
      }

      setFilteredEntries(
        filtered.map((item, idx) => ({ ...item, rank: idx + 1 }))
      );
    },
    [sortBy, currentUser]
  );

  useEffect(() => {
    if (entries.length) applyFilter(currentFilter, entries);
  }, [entries, currentFilter, sortBy, applyFilter]);

  const handleAddEntry = async (newEntry, initialRating) => {
    try {
      const created = await addEntry(newEntry, initialRating, currentUser);
      const normalizedCreated = normalizeEntries([created])[0];
      setEntries([...entries, normalizedCreated]);
      setShowAddModal(false);
    } catch (error) {
      console.error('Fehler beim Erstellen:', error);
      alert('Fehler beim Erstellen des Eintrags');
    }
  };

  const handleEditEntry = async (updatedEntry, rating) => {
    try {
      const numericRating =
        typeof rating === 'number' ? rating : parseFloat(rating) || 0;
      const updated = await updateEntry(
        updatedEntry.id,
        updatedEntry,
        numericRating,
        currentUser
      );
      const normalizedUpdated = normalizeEntries([updated])[0];
      setEntries(
        entries.map(e => (e.id === normalizedUpdated.id ? normalizedUpdated : e))
      );
      setEditingEntry(null);
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      alert('Fehler beim Aktualisieren des Eintrags');
    }
  };

  const handleDeleteEntry = async (id) => {
    setDeleteConfirm({ id, name: entries.find(e => e.id === id)?.name });
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        await deleteEntry(deleteConfirm.id);
        setEntries(entries.filter(e => e.id !== deleteConfirm.id));
        setDeleteConfirm(null);
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
        alert('Fehler beim Löschen des Eintrags');
      }
    }
  };

  const handleUserRating = async (entryId, rating, closePopup) => {
    try {
      const numericRating =
        typeof rating === 'number' ? rating : parseFloat(rating) || 0;
      const updatedEntry = await updateEntry(
        entryId,
        { user_ratings: { [currentUser]: numericRating } },
        numericRating,
        currentUser
      );
      const normalizedUpdated = normalizeEntries([updatedEntry])[0];
      setEntries(entries.map(e => (e.id === entryId ? normalizedUpdated : e)));
      if (closePopup) closePopup(false);
    } catch (error) {
      console.error('Fehler beim Bewerten:', error);
      alert('Fehler beim Aktualisieren der Bewertung');
    }
  };

  const handleCreateTag = async (newTag) => {
    try {
      const created = await createTag(newTag);
      setAllTags([...allTags, created]);
    } catch (error) {
      console.error('Fehler beim Erstellen des Tags:', error);
      alert('Fehler beim Erstellen des Tags');
    }
  };

  // ← A10: Entries nach deleteTag frisch laden
  const handleDeleteTag = async (tag) => {
    if (!window.confirm(`Tag "${tag.name}" wirklich löschen?`)) return;
    try {
      await deleteTag(tag.name);

      const [freshEntries, freshTags] = await Promise.all([
        fetchEntries(),
        fetchTags(),
      ]);
      setEntries(normalizeEntries(freshEntries));
      setAllTags(freshTags);
      setCurrentFilter(prev => ({
        ...prev,
        tags: prev.tags.filter(t => t.name !== tag.name),
      }));
    } catch (error) {
      console.error('Fehler beim Löschen des Tags:', error);
      alert('Fehler beim Löschen des Tags');
    }
  };

  const handleSearch = (searchParams) => {
    const newFilter = { ...currentFilter, ...searchParams };
    setCurrentFilter(newFilter);
    setShowSearchModal(false);
  };

  const handleRandom = (withTagRestriction = false) => {
    let filtered = [...entries];
    if (currentFilter.type !== 'both')
      filtered = filtered.filter(e => e.type === currentFilter.type);
    if (currentFilter.term?.trim()) {
      const term = currentFilter.term.toLowerCase();
      filtered = filtered.filter(
        e =>
          e.name.toLowerCase().includes(term) ||
          (e.description || '').toLowerCase().includes(term)   // ← Guard
      );
    }
    if (withTagRestriction && currentFilter.tags?.length) {
      if (currentFilter.matchMode === 'and') {
        filtered = filtered.filter(e =>
          currentFilter.tags.every(filterTag =>
            e.tags.some(t => t.name === filterTag.name)
          )
        );
      } else {
        filtered = filtered.filter(e =>
          currentFilter.tags.some(tag =>
            e.tags.some(t => t.name === tag.name)
          )
        );
      }
    }
    if (filtered.length) {
      const randomIndex = Math.floor(Math.random() * filtered.length);
      setRandomEntry(filtered[randomIndex]);
    } else {
      alert('Keine Einträge gefunden, die den Kriterien entsprechen.');
    }
  };

  const handleExport = () => {
    let content = `film-favs Export\n${new Date().toLocaleString()}\n\n`;
    filteredEntries.forEach(entry => {
      content += `#${entry.rank} ${entry.name} (${
        entry.type === 'film' ? 'Film' : 'Serie'
      })\n`;
      content += `Ø Bewertung: ${entry.current_ranking.toFixed(1)}%\n`;
      const myRating = entry.user_ratings?.[currentUser];
      if (myRating) content += `Meine Bewertung: ${myRating.toFixed(1)}%\n`;
      if (entry.tags?.length)
        content += `Tags: ${entry.tags.map(t => t.name).join(', ')}\n`;
      if (entry.description)
        content += `Beschreibung: ${entry.description}\n`;
      content += '\n';
    });
    const blob = new Blob([content], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `filmfavs_${new Date().toISOString().slice(0, 19)}.txt`;
    link.click();
  };

  if (loading)
    return <div className={styles.layoutContainer}>Laden...</div>;
  if (randomEntry)
    return (
      <RandomViewModal
        entry={randomEntry}
        users={users}
        currentUser={currentUser}
        onClose={() => setRandomEntry(null)}
        onRate={handleUserRating}
      />
    );

  const existingNames = entries.map(e => e.name.toLowerCase());

  return (
    <div className={styles.layoutContainer}>
      <div className={styles.layoutHeader}>
        <h1 className={styles.title}>film-favs</h1>
        <div className={styles.layoutHeaderActions}>
          <button
            className={`${styles.compSortBtn} ${
              sortBy === 'average' ? styles.active : ''
            }`}
            onClick={() =>
              setSortBy(sortBy === 'average' ? 'user' : 'average')
            }
          >
            {sortBy === 'average' ? <FiTrendingUp /> : <FiUser />}
            <span className={styles.compSortLabel}>
              {sortBy === 'average' ? '# Ø' : '# Meine'}
            </span>
          </button>
          <button
            className={styles.compRandomBtn}
            onClick={() => handleRandom(false)}
            title="Zufälliger Eintrag"
          >
            <FiShuffle />
          </button>
          <button
            className={styles.compRandomRestrictedBtn}
            onClick={() => handleRandom(true)}
            title="Zufall mit aktuellen Filtern"
          >
            <FiShuffle /> <FiAward size={12} />
          </button>
          <button
            className={styles.compExportBtn}
            onClick={handleExport}
            title="Export als Text"
          >
            <FiFileText /> Export
          </button>
          <button
            className={styles.compSearchBtn}
            onClick={() => setShowSearchModal(true)}
          >
            <FiSearch />
          </button>
          <button
            className={styles.compAddBtn}
            onClick={() => setShowAddModal(true)}
          >
            <FiPlus />
          </button>
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.typeFilter}>
          <button
            className={`${styles.layoutFilterBtn} ${
              currentFilter.type === 'both' ? styles.active : ''
            }`}
            onClick={() => handleSearch({ type: 'both' })}
          >
            both
          </button>
          <button
            className={`${styles.layoutFilterBtn} ${
              currentFilter.type === 'film' ? styles.active : ''
            }`}
            onClick={() => handleSearch({ type: 'film' })}
          >
            films
          </button>
          <button
            className={`${styles.layoutFilterBtn} ${
              currentFilter.type === 'series' ? styles.active : ''
            }`}
            onClick={() => handleSearch({ type: 'series' })}
          >
            series
          </button>
        </div>
        {(currentFilter.term || currentFilter.tags?.length > 0) && (
          <div className={styles.activeFilters}>
            {currentFilter.term && (
              <Chip
                label={`Suche: ${currentFilter.term}`}
                category="misc"
                onRemove={() =>
                  handleSearch({ ...currentFilter, term: '' })
                }
              />
            )}
            {currentFilter.tags.map(tag => (
              <Chip
                key={tag.name}
                label={tag.name}
                category={tag.category}
                onRemove={() =>
                  handleSearch({
                    ...currentFilter,
                    tags: currentFilter.tags.filter(
                      t => t.name !== tag.name
                    ),
                  })
                }
              />
            ))}
          </div>
        )}
      </div>

      <div className={styles.entriesList}>
        {filteredEntries.length === 0 ? (
          <div className={styles.noResults}>
            <p>Keine Einträge gefunden.</p>
            <button
              onClick={() => setShowAddModal(true)}
              className={styles.addFirstBtn}
            >
              <FiPlus /> Ersten Eintrag erstellen
            </button>
          </div>
        ) : (
          filteredEntries.map(entry => (
            <div
              key={entry.id}
              id={`entry-${entry.id}`}
              className={styles.entryWrapper}
            >
              <div className={styles.rankNumber}>#{entry.rank}</div>
              <FilmEntry
                entry={entry}
                currentUser={currentUser}
                users={users}
                onEdit={setEditingEntry}
                onDelete={handleDeleteEntry}
                onUserRating={handleUserRating}
                onShowRatings={() =>
                  setShowRatingsPopup(entry.user_ratings)
                }
              />
            </div>
          ))
        )}
      </div>

      {showAddModal && (
        <EntryModal
          onSave={handleAddEntry}
          onClose={() => setShowAddModal(false)}
          existingNames={existingNames}
          allTags={allTags}
          currentUser={currentUser}
          onCreateTag={handleCreateTag}
          onDeleteTag={handleDeleteTag}
        />
      )}
      {editingEntry && (
        <EntryModal
          onSave={handleEditEntry}
          onClose={() => setEditingEntry(null)}
          initialData={editingEntry}
          existingNames={existingNames.filter(
            n => n !== editingEntry.name.toLowerCase()
          )}
          allTags={allTags}
          currentUser={currentUser}
          onCreateTag={handleCreateTag}
          onDeleteTag={handleDeleteTag}
        />
      )}
      {showSearchModal && (
        <SearchModal
          onSearch={handleSearch}
          onClose={() => setShowSearchModal(false)}
          allTags={allTags}
          onCreateTag={handleCreateTag}
          onDeleteTag={handleDeleteTag}
        />
      )}
      {showRatingsPopup && (
        <UserRatingsPopup
          ratings={showRatingsPopup}
          users={users}
          onClose={() => setShowRatingsPopup(null)}
        />
      )}
      {deleteConfirm && (
        <DeleteConfirmModal
          entryName={deleteConfirm.name}
          onConfirm={confirmDelete}
          onClose={() => setDeleteConfirm(null)}
        />
      )}

      <ScrollToTopButton />
    </div>
  );
};

export default FilmFavs;